import React, {useState, useEffect, useCallback, useMemo, useRef} from "react";
import {HashRouter, Route, Switch, Redirect} from "react-router-dom";
import caretActive from "./images/caret-active.svg";
import caretInactive from "./images/caret-inactive.svg";
import closeIcon from "./images/close.svg";

import wrs from "./images/m1_logo.png";
import marker from "./images/marker.svg";

import plotigoLogo from "./images/plotigo-logo.svg";
import wtLogo from "./images/wt-logo.png";
import exclamation from "./images/exclamation.png";

import moment from "moment";
import _ from "lodash";
import {Provider} from "react-redux";
import configStore from "./redux/configStore";
import {bindActionCreators} from "redux";
import {connect} from "react-redux";
import {getDateByNTP} from "./tools/getNtp";
import GoogleMapReact, { fitBounds } from "google-map-react";

import {
  updateBusLocation,
  getBusETA,
  updateEta,
  shouldUpdateData,
  getRouteTags,
  getAllEta,
  getAllEtaSuccess,
  setNtp,
  setNtpSuccess,
} from "./redux/action";

import {colors} from "./colors";
import "./App.css";
import debugLog, {LogLevel} from "./utils/debugLog";

const {store} = configStore();

const signalR = require("@microsoft/signalr");

const connection = new signalR.HubConnectionBuilder()
  .withUrl(`${process.env.REACT_APP_SERVER_URL}/SignalRHubs/RealtimeVehicle`, {
    accessTokenFactory: () => "",
    transport: signalR.HttpTransportType.WebSockets,
    skipNegotiation: true,
  })
  .configureLogging(signalR.LogLevel.Information)
  .build();

const useViewport = () => {
  const [width, setWidth] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const handleWindowResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  // Return the width so we can use it in our components
  return {width};
};

const mapStateToProps = ({main}: any) => ({
  busData: main.busData,
  locations: main.locations,
  routeTags: main.routeTags,
  allEta: main.allEta,
  ntp: main.ntp,
});

const mapDispatchToProps = (dispatch: any) => {
  return bindActionCreators(
    {
      getBusETA,
      updateBusLocation,
      updateEta,
      shouldUpdateData,
      getRouteTags,
      getAllEta,
      getAllEtaSuccess,
      setNtp,
      setNtpSuccess,
    },
    dispatch
  );
};

const Main = connect(
  mapStateToProps,
  mapDispatchToProps
)(({busData, serviceType = 'trunk', ...props}: any) => {
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [activeRouteId, setActiveRouteId] = useState<number>(-1);
  const [locations, setLocations] = useState<any>([]);
  const [isConnected, setConnected] = useState(false);
  const [routeToSubscribe, setRouteToSubscribe] = useState<any[]>([]);
  const routeTagsMemo = useMemo(() => props.routeTags, [props.routeTags]);
  const locationsMemo = useMemo(() => locations, [locations]);
  const [isAllRoute, setIsAllRoute] = useState<boolean>(false);
  const [AllRoute, setAllRoute] = useState<any>(null);
  const [now, setNow] = useState<any>(null);
  const [hasLoadedRouteTags, setHasLoadedRouteTags] = useState(false);

  // Track when route tags have been loaded at least once
  useEffect(() => {
    if (props.routeTags && props.routeTags.length > 0) {
      setHasLoadedRouteTags(true);
    }
  }, [props.routeTags]);

  const { width } = useViewport();

  // Initial assigning the diff of ntp value to current device time to the state
  useEffect(() => {
    getDateByNTP().then((time: any) => {
      const toMoment = moment(time.datetime);
      const diff = moment().diff(toMoment);
      const diffInMinutes = moment.duration(diff).asMinutes();

      props.setNtp(-diffInMinutes);
    });
  }, []);

  //Initial Setup. Begin Socket Connection and Get Route Tags Data
  useEffect(() => {
    startConnection();
    props.getRouteTags();
  }, []);

  //Listen for Route Tags props changes. When changed, populate RouteID Data and set List of route to be subscribed. set to state.
  useEffect(() => {
    if (!props.routeTags || props.routeTags.length === 0) return;

    const routeToSub: RouteID[] = [];

    props.routeTags.forEach((routeTag: any) => {
      routeTag.routes.forEach((route: any) => {
        routeToSub.push({ id: route.routeId, routeLocationIds: [] });
      });
    });

    setRouteToSubscribe((prev: any[]) => {
      if (_.isEqual(prev, routeToSub)) return prev;
      return routeToSub;
    });
    if (width >= 1130 && routeToSub.length > 0) {
      setActiveIndex(0);
      setActiveRouteId(routeToSub[0].id as number);
    } else {
      setActiveIndex(-1);
      setActiveRouteId(-1);
    }
  }, [props.routeTags, width]);

  // Subscribe to the route and get the bus data
  async function startConnection() {
    try {
      await connection.stop();
      await connection.start();

      connection.on("BroadcastEta", (data: any) => {
        props.updateBusLocation(data);
      });

      setConnected(true);

      connection.onclose(() => {
        setConnected(false);
        connection.stop();
        startConnection();
      });

    } catch (err) {
      // Send log
      debugLog(LogLevel.ERROR, 'Unable to get ETAs. ', {
        err: err,
      });
      setConnected(false);
      startConnection();
    }
  };

  // Listen for the connection status and update the state
  useEffect(() => {
    if (isConnected && routeToSubscribe.length !== 0) {
      props.getBusETA(routeToSubscribe, serviceType);
      setAllRoute(routeToSubscribe);
      routeToSubscribe.forEach(async (route: RouteID) => {
        try {
          await connection.invoke("JoinGroup", `route:${route.id}`);
        } catch (err) {
          // Send log
          debugLog(LogLevel.ERROR, 'Unable to get bus ETA. ', {
            err: err,
            routeToSubscribe: routeToSubscribe,
          });
          throw err;
        }
      });
    }
  }, [isConnected, routeToSubscribe]);

  // Listen for the route tags and update the state
  useEffect(() => {
    if (AllRoute !== null && isAllRoute === false) {
      props.getAllEta(AllRoute);
      setIsAllRoute(() => true);
    }
  }, [AllRoute, isAllRoute]);

  // Listen for the route tags and update the state
  useEffect(() => {
    const activeRouteTags = props.routeTags[activeIndex];

    if (activeRouteTags) {
      const activeRouteIds = _.map(activeRouteTags.routes, (route) => route.routeId);

      let colorIndexSelector = 0;

      const displayLocations = _.filter(props.locations, (location) =>
        location.routeIds.some((o: Number) => activeRouteIds.includes(o))
      );

      // TODO:
      // Find the data that has different routeId than activeRouteIds (activeRouteIds is array but the value should only one), save its locationId
      const differentRouteLocationId = _.filter(
        displayLocations,
        (location) => location.routeId !== activeRouteIds[0]
      )[0]?.locationId;

      // Then get one data from displayLocations that has routeId same with first item in activeRouteIds
      const displayLocationSameRouteId = _.filter(
        displayLocations,
        (location) => location.routeId === activeRouteIds[0]
      )[0];

      // Get the routeLocations. Find the locationId that same with the saved locationId, and get the order prop
      const routeLocsFromSameRouteId = displayLocationSameRouteId?.routeLocations?.find(
        (routeLoc: any) => routeLoc.locationId === differentRouteLocationId
      );
      const newOrder = routeLocsFromSameRouteId?.order;

      // Replace the order data value on item that has different routeId than activeRouteIds with the order that got from routeLocations
      if (newOrder !== undefined) {
        displayLocations.forEach((displayLocation) => {
          if (displayLocation.routeId !== activeRouteIds[0]) {
            displayLocation.order = newOrder;
          }
        });
      }

      displayLocations.forEach((displayLocation) => {
        displayLocation.color = colors[colorIndexSelector];
        colorIndexSelector++;

        const locationBusData = _.filter(
          busData,
          (o) =>
            // o.toLocation.id === displayLocation.locationId && activeRouteIds.includes(o.route.id)
            o.toLocation.id === displayLocation.locationId
        );

        displayLocation.busData = _.orderBy(locationBusData, (item: any) => {
          return moment(item.eta);
        });
      });
     
      setLocations(displayLocations);
    }
  }, [busData, activeIndex, props.locations]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(moment().add(props.ntp, "minutes"));
    }, 1000);
    return () => clearInterval(interval);
  }, [locations, now]);

  // Render the component
  return (
    <div className="App">
      <ComponentRender
        routeTagsMemo={routeTagsMemo}
        locationsMemo={locationsMemo}
        setActiveIndex={setActiveIndex}
        activeIndex={activeIndex}
        setActiveRouteId={setActiveRouteId}
        activeRouteId={activeRouteId}
        ntp={props.ntp}
        compareTime={now}
        serviceType={serviceType}
        hasLoadedRouteTags={hasLoadedRouteTags}
      />
    </div>
  );
});

// Render the component
const BusMarker = React.memo<any>(({remaining, color}: any) => {
  const getETA = useCallback(() => {
    if (remaining < 1 && remaining > 0.12) {
      return (
        <div
          className="Marker-ETA-Arriving"
          style={{backgroundColor: color}}
        >{`ARRIVING`}</div>
      );
    } else if (remaining <= 0.12) {
      return (
        <div
          className="Marker-ETA-Arr"
          style={{backgroundColor: color}}
        >{`ARRIVED`}</div>
      );
    } else {
      return (
        <div
          className="Marker-ETA"
          style={{backgroundColor: color}}
        >{`${Math.floor(remaining)}min`}</div>
      );
    }
  }, [color, remaining]);

  return (
    <div className="Marker-Wrapper">
      {getETA()}
      <img className="Marker-Icon" src={marker} alt="Bus Marker" />
    </div>
  );
});

// Render the component
export const FlagMarker = React.memo<any>((props: any) => {
  return (
    <div className="Marker-Wrapper-Flag">
      <div>
        <svg
          width="41"
          height="44"
          viewBox="0 0 41 44"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M41 20.5C41 31.8218 31.8218 41 20.5 41C9.17816 41 0 31.8218 0 20.5C0 9.17816 9.17816 0 20.5 0C31.8218 0 41 9.17816 41 20.5Z"
            fill={`${props.color ? props.color : "#016901"}`}
          />
          <path
            d="M21 44L24.4641 40.25H17.5359L21 44Z"
            fill={`${props.color ? props.color : "#016901"}`}
          />
          <g clipPath="url(#clip0)">
            <path
              d="M22.8125 19.0625H28.0625C28.304 19.0625 28.5 18.8665 28.5 18.625C28.5 18.3835 28.304 18.1875 28.0625 18.1875H22.8125C22.571 18.1875 22.375 18.3835 22.375 18.625C22.375 18.8665 22.571 19.0625 22.8125 19.0625Z"
              fill="white"
            />
            <path
              d="M28.9375 19.9375H21.9375C21.696 19.9375 21.5 20.1335 21.5 20.375V25.625C21.5 25.8665 21.696 26.0625 21.9375 26.0625H28.9375C29.179 26.0625 29.375 25.8665 29.375 25.625V20.375C29.375 20.1335 29.179 19.9375 28.9375 19.9375ZM28.5 25.1875H22.375V20.8125H28.5V25.1875H28.5Z"
              fill="white"
            />
            <path
              d="M31.5625 19.0625C31.321 19.0625 31.125 19.2585 31.125 19.5V18.625C31.125 17.4193 30.1432 16.4375 28.9375 16.4375H21.9375C20.7318 16.4375 19.75 17.4193 19.75 18.625V19.5C19.75 19.2585 19.554 19.0625 19.3125 19.0625C19.071 19.0625 18.875 19.2585 18.875 19.5V23C18.875 23.2415 19.071 23.4375 19.3125 23.4375C19.554 23.4375 19.75 23.2415 19.75 23V28.25C19.75 28.4915 19.946 28.6875 20.1875 28.6875H20.6251C20.6251 29.1696 21.0179 29.5625 21.5 29.5625H22.375C22.8572 29.5625 23.25 29.1696 23.25 28.6875H27.625C27.625 29.1696 28.0179 29.5625 28.5 29.5625H29.375C29.8571 29.5625 30.25 29.1696 30.25 28.6875H30.6875C30.929 28.6875 31.125 28.4915 31.125 28.25V23C31.125 23.2415 31.321 23.4375 31.5625 23.4375C31.804 23.4375 32 23.2415 32 23V19.5C32 19.2585 31.804 19.0625 31.5625 19.0625ZM30.25 27.8125H20.625V18.625C20.625 17.9013 21.2139 17.3125 21.9375 17.3125H28.9375C29.6612 17.3125 30.25 17.9013 30.25 18.625V27.8125H30.25Z"
              fill="white"
            />
            <path
              d="M15.8125 26.9374H14.0625C13.3389 26.9374 12.75 27.5262 12.75 28.2499V29.1249C12.75 29.3664 12.946 29.5624 13.1875 29.5624C13.429 29.5624 13.625 29.3664 13.625 29.1249V28.6874H16.25V29.1249C16.25 29.3664 16.446 29.5624 16.6875 29.5624C16.929 29.5624 17.125 29.3664 17.125 29.1249V28.2499C17.125 27.5263 16.5361 26.9374 15.8125 26.9374Z"
              fill="white"
            />
            <path
              d="M20.1875 15.5625C20.429 15.5625 20.625 15.3665 20.625 15.125V14.25C20.625 11.5961 18.4664 9.4375 15.8125 9.4375C13.1587 9.4375 11 11.5961 11 14.25V29.125C11 29.3665 11.196 29.5625 11.4375 29.5625C11.679 29.5625 11.875 29.3665 11.875 29.125V19.7572L19.4254 15.5625H20.1875ZM11.875 14.25C11.875 12.0791 13.6416 10.3125 15.8125 10.3125C17.9834 10.3125 19.75 12.0791 19.75 14.25V14.6875H11.875V14.25ZM11.875 18.7563V15.5625H17.6237L11.875 18.7563Z"
              fill="white"
            />
          </g>
          <defs>
            <clipPath id="clip0">
              <rect
                width="21"
                height="21"
                fill="white"
                transform="translate(11 9)"
              />
            </clipPath>
          </defs>
        </svg>
      </div>
    </div>
  );
});

// Render the component
const ComponentRender = React.memo<any>(
  ({routeTagsMemo, locationsMemo, setActiveIndex, setActiveRouteId,
    activeRouteId, activeIndex, ntp, compareTime, serviceType, hasLoadedRouteTags}: any) => {
    const mapLocationSourceRef = useRef<HTMLDivElement | null>(null);
    const [showModal, setShowModal] = useState(false);

    const [mapInstance, setMapInstance] = useState<any>(null);
    const [mapsInstance, setMapsInstance] = useState<any>(null);

    const routeTags = useMemo(() => {
      if (!routeTagsMemo) return [];
      
      return routeTagsMemo.filter((tag: any) => {
        const isTagShuttle = /\(shuttle\)/i.test(tag.name);
        return serviceType === 'shuttle' ? isTagShuttle : !isTagShuttle;
      });
    }, [routeTagsMemo, serviceType]);
    const locations = useMemo(() => {
      return locationsMemo;
    }, [locationsMemo]);
    const {width} = useViewport();

    const handleApiLoaded = ({ map, maps }: any) => {
      setMapInstance(map);
      setMapsInstance(maps);
    };

    // const polyString: string =
    //   "a~bGapexRg@r@e@h@cA|@KPKRBDR\\XhA?r@Gh@IVSb@H|@zA`ATHPEf@S`GyJb@o@DQZc@rAyAhBqBfA_Ad@[bAg@jAc@^Yl@]`@[POCG?EBMHKLCIyAGkAqBLmAJaCNW?e@S]V[Ja@CYKQQKc@?MH]HQPKh@IR@^NVYPEbAIzBQpAKn@IRCCYCw@Aa@?WDOZc@b@WrAk@jEiBd@SRIHIf@MfASAPAhA]nBOv@c@rBQJi@HSJS^IXKPWD{BLkAD?FEVKTSPWLQDqBLcCR{AHWGMIGCMJKHYJ]@_@MQMM[AUBUDMQOiA{@{@m@QEG?]RUFI?UEWUqBzBcBvBkClDaAlAy@`AoA`BGFEZC`@?Zr@d@~AdA~AdAd@`@T`@Nd@Jh@@^JZDDLBRIVSXc@rBiD|BwDpAeC@OAQcBiAc@FIHmBtDs@rA";

    // Comment this to hide the polyline at the start
    // const handleApiLoaded = ({map, maps}: any) => {
    //   // Define the LatLng coordinates for the polyline's path
    //   const polylinePath = polyline.decode(polyString as string);

    //   const transformedCoordinates = polylinePath.map((coords: any) => ({
    //     lat: coords[0],
    //     lng: coords[1],
    //   }));

    //   // Create a new polyline using the Google Maps API
    //   const polylines = new maps.Polyline({
    //     path: transformedCoordinates,
    //     geodesic: true,
    //     strokeColor: "#2A6BEB",
    //     strokeOpacity: 1.0,
    //     strokeWeight: 3,
    //   });

    //   // Set the polyline on the map
    //   polylines.setMap(map);
    // };

    const changeActiveIndex = useCallback(
      (index: number, routeId?: number) => {
        setActiveIndex(index === activeIndex ? (width >= 1130 ? index : -1) : index);
        if (routeId) {
          setActiveRouteId(routeId);
        }
      },
      [activeIndex, setActiveIndex, setActiveRouteId, width]
    );

    // useEffect(() => {
    //   if (activeRouteId) {
    //     const getPolylineString = polylineRoutes.find(
    //       (item: any) => item.routeId === activeRouteId
    //     )?.polylineString;
    //     setPolylineString(getPolylineString || '');
    //   }
    // }, [activeRouteId]);

    // 3️⃣ Fit map to bounds based on locations
    const hasBoundedRef = useRef(false);
    const lastRouteIdRef = useRef<string | null>(null);

    useEffect(() => {
      if (!mapInstance || !locations || locations.length === 0) return;
      
      // Check if we should re-bound the map
      const shouldBound = !hasBoundedRef.current || lastRouteIdRef.current !== activeRouteId;
      
      if (!shouldBound) return;

      // Ensure that the loaded locations belong to the activeRouteId
      // Otherwise `locations` from the PREVIOUS route are briefly rendered here and bounded 
      const locationsBelongToActiveRoute = locations.some((l: any) => 
        l.routeIds && l.routeIds.includes(activeRouteId)
      );
      if (!locationsBelongToActiveRoute && locations.length > 0) {
          return;
      }
      
      const lats = locations.map((l: any) => l.location.lat);
      const lngs = locations.map((l: any) => l.location.lng);
      const bounds = {
        ne: { lat: Math.max(...lats), lng: Math.max(...lngs) },
        sw: { lat: Math.min(...lats), lng: Math.min(...lngs) },
      };

      const mapDiv = mapInstance.getDiv();
      const size = {
        width: mapDiv.offsetWidth,
        height: mapDiv.offsetHeight,
      };

      const { center } = fitBounds(bounds, size);
      mapInstance.setCenter(center);
      mapInstance.setZoom(14);
      
      // Update refs
      hasBoundedRef.current = true;
      lastRouteIdRef.current = activeRouteId;
    }, [locations, mapInstance, activeRouteId]);

    const firstArrivedBus = (() => {
      for (let i = 0; i < (locations?.length || 0); i++) {
        const loc = locations[i];
        if (!Array.isArray(loc?.busData)) continue;
        for (let j = 0; j < loc.busData.length; j++) {
          const bus = loc.busData[j];
          if (bus?.assignmentLocation?.status === 'arrived' && !bus.hide) {
            return bus;
          }
        }
      }
      return undefined;
    })();

    const targetFromLocationId = firstArrivedBus?.fromLocation?.id ?? null;
    const targetFromLocationIdColor = locations.find(
      (loc: any) => loc.location.id === targetFromLocationId
    )?.color;

    return (
      <>
        {width >= 1130 ? (
          <header
            className="Navbar"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "-30px",
            }}
          >
            <img className="Logo" src={wrs} alt="Logo" />
            <div style={{marginTop: "15px", marginRight: "5%"}}>
              <div
                className="Notice"
                style={{width: "400px", marginBottom: "10px"}}
              >
                <img
                  style={{height: "25px", marginRight: "10px"}}
                  alt="notice"
                  src={exclamation}
                />
                <span>
                  <b>Please note</b>: The color atop the bus icon signifies its
                  next stop. Please prepare accordingly.
                </span>
              </div>
              <div className="Notice" style={{width: "400px"}}>
                <img
                  style={{height: "25px", marginRight: "10px"}}
                  alt="notice"
                  src={exclamation}
                />
                <span>
                  Kindly refresh every <b>3 minutes</b> to view the latest updates/ETA.
                </span>
              </div>
            </div>
          </header>
        ) : (
          <header
            className="Navbar"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              margin: "25px",
            }}
          >
            <img
              className="Logo"
              src={wrs}
              alt="Logo"
              style={{marginBottom: "15px"}}
            />
            <div>
              <div
                className="Notice"
                style={{marginBottom: "10px", width: "100%"}}
              >
                <img
                  style={{height: "25px", marginRight: "10px"}}
                  alt="notice"
                  src={exclamation}
                />
                <span>
                  <b>Please note</b>: The color atop the bus icon signifies its
                  next stop. Please prepare accordingly.
                </span>
              </div>
              <div className="Notice" style={{width: "100%"}}>
                <img
                  style={{height: "25px", marginRight: "10px"}}
                  alt="notice"
                  src={exclamation}
                />
                <span>
                  Kindly refresh every <b>3 minutes</b> to view the latest updates/ETA.
                </span>
              </div>
            </div>
          </header>
        )}
        <div className="Wrapper">
          <div className="Selection-Wrapper">
            <h3 className="Route-Title">{serviceType === 'shuttle' ? 'Shuttle Service' : 'Trunk Service'}</h3>
            {_.isEmpty(routeTags) ? (
              <div className="Title-Loading-Wrapper">
                {hasLoadedRouteTags ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                    No {serviceType === 'shuttle' ? 'shuttle' : 'trunk'} service routes available.
                  </div>
                ) : (
                  <div className="lds-ellipsis">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                )}
              </div>
            ) : (
              routeTags.map((item: any, index: number) => {
                return (
                  <div
                    key={item.id}
                    className={`Route-Button ${activeIndex === index ? "active" : ""
                      }  `}
                  >
                    <div
                      className="Button-Text-Wrapper"
                      onClick={() => {
                        changeActiveIndex(index, item.routes[0]?.routeId);
                      }}
                    >
                      {serviceType === 'shuttle' ? item.name.replace(/\s*\([Ss]huttle\)\s*/i, '').trim() : item.name}
                      {activeIndex === index ? (
                        <img
                          alt="caret-active"
                          className="Caret-Icon"
                          src={caretActive}
                        />
                      ) : (
                        <img
                          alt="caret-inactive"
                          className="Caret-Icon"
                          src={caretInactive}
                        />
                      )}
                    </div>

                    {activeIndex === index && width < 1130 && (
                      <>
                        <div className="Mini-Map">
                          <GoogleMapReact
                            bootstrapURLKeys={{
                              key: 'AIzaSyDYGvy_HvsUfvJ_7_d4PmOH201jIFNn-iM',
                            }}
                            defaultCenter={{
                              lat: 1.328423,
                              lng: 103.746267,
                            }}
                            yesIWantToUseGoogleMapApiInternals
                            onGoogleApiLoaded={handleApiLoaded}
                            defaultZoom={activeIndex === 0 ? 14 : 13}
                          >
                            {locations
                              .sort((a: any, b: any) => a.order - b.order)
                              .map((location: any) => {
                              const now = moment().add(ntp, 'minutes');
                              let remaining: number | null = null;

                              // 1️⃣ Find smallest ETA for the active route
                              const smallestEta =
                                location?.etas
                                  ?.filter(
                                    (eta: any) =>
                                      eta &&
                                      eta.eta &&
                                      eta.route.id === activeRouteId &&
                                      compareTime.diff(moment(eta.expiredAt), 'minutes') < 1
                                  )
                                  ?.reduce((min: any, eta: any) => {
                                    if (min === null) return eta;
                                    return moment(eta.eta).isBefore(moment(min.eta)) ? eta : min;
                                  }, null) || null;

                              // 2️⃣ Calculate remaining minutes
                              if (smallestEta && smallestEta.eta !== null) {
                                remaining = moment
                                  .duration(moment(smallestEta.eta).diff(now))
                                  .asMinutes();
                              }

                              // 3️⃣ Determine ETA states
                              const hasEta = typeof remaining === 'number' && remaining > 0;
                              const isArriving =
                                typeof remaining === 'number' && remaining < 1 && remaining > 0.12;
                              const isArrived = typeof remaining === 'number' && remaining <= 0.12;
                              const noEta = !hasEta && !isArriving && !isArrived;

                              // 4️⃣ Determine color
                              // Grey if: No ETA OR busData empty
                              // Keep color if ETA, ARRIVING, or ARRIVED
                              const markerColor =
                                noEta || _.isEmpty(location.busData) ? '#a6a6a6' : location.color;

                              // 5️⃣ Render FlagMarker
                              return (
                                <FlagMarker
                                  key={location.id}
                                  color={markerColor}
                                  lat={location.location.lat}
                                  lng={location.location.lng}
                                />
                              );
                            })}

                            {locations
                              .sort((a: any, b: any) => a.order - b.order)
                              .map((busLocation: any) => {
                              const smallestEta = busLocation?.etas
                                ?.filter((eta: any) => eta && eta.eta)
                                ?.reduce((min: any, eta: any) => {
                                  if (min === null) return eta;
                                  return moment(eta.eta).isBefore(moment(min.eta)) ? eta : min;
                                }, null);

                              return busLocation?.busData?.map((bus: any, i: number) => {
                                const now = moment().add(ntp, 'minutes');
                                const diff = compareTime.diff(
                                  moment(bus.vehicle?.expiredAt),
                                  'minutes'
                                );

                                // Calculate ETA or remaining time
                                const adjustedETA =
                                  smallestEta === null ? moment(bus.eta) : moment(smallestEta?.eta);
                                const remaining = moment
                                  .duration(adjustedETA.diff(now))
                                  .asMinutes();

                                // 🚫 Skip if expired
                                if (!bus.hide && diff === 2) return <></>;
                                if (bus.hide || activeRouteId !== bus.route.id) return <></>;

                                // Determine if this bus should show "ARRIVED"
                                const isPrevStopOfArrivedBus =
                                  targetFromLocationId !== null &&
                                  targetFromLocationId === bus.fromLocation?.id;

                                return (
                                  <BusMarker
                                    remaining={remaining}
                                    color={busLocation.color}
                                    bus={bus}
                                    key={i}
                                    lat={bus?.vehicle?.lastLocationLat}
                                    lng={bus?.vehicle?.lastLocationLng}
                                    isPrevStopOfArrivedBus={isPrevStopOfArrivedBus}
                                    targetFromLocationIdColor={
                                      targetFromLocationIdColor || busLocation.color
                                    }
                                  />
                                );
                              });
                            })}
                          </GoogleMapReact>
                        </div>
                        <div className="Route-Wrapper-Mini">
                          <div className="Route-Header">
                            <div className="Text-Wrapper">
                              <span className="Text-Title">Pick-up</span>
                              <div className="Column-Flex">
                                <span className="Text-Detail">Estimated</span>
                                <span className="Text-Detail">Time of Arrival</span>
                              </div>
                            </div>
                          </div>
                          <div className="Route-Body">
                            {_.isEmpty(locations) ? (
                              <div
                                className="Title-Loading-Wrapper"
                                style={{ alignItems: 'center', justifyContent: 'center' }}
                              >
                                {hasLoadedRouteTags && _.isEmpty(routeTags) ? (
                                  <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                                    No {serviceType === 'shuttle' ? 'shuttle' : 'trunk'} service routes available.
                                  </div>
                                ) : (
                                  <div className="lds-ellipsis" style={{ marginLeft: '-40px' }}>
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              locations
                                .sort((a: any, b: any) => a.order - b.order)
                                .map((location: any) => {
                                const now = moment().add(ntp, 'minutes');
                                let remaining: number | null = null;

                                // Find smallest ETA
                                const smallestEta =
                                  location?.etas
                                    ?.filter(
                                      (eta: any) =>
                                        eta &&
                                        eta.eta &&
                                        eta.route.id === activeRouteId &&
                                        compareTime.diff(moment(eta.expiredAt), 'minutes') < 1
                                    )
                                    ?.reduce((min: any, eta: any) => {
                                      if (min === null) return eta;
                                      return moment(eta.eta).isBefore(moment(min.eta)) ? eta : min;
                                    }, null) || null;

                                if (smallestEta && smallestEta.eta !== null) {
                                  remaining = moment
                                    .duration(moment(smallestEta.eta).diff(now))
                                    .asMinutes();
                                }

                                // States
                                const hasEta = typeof remaining === 'number' && remaining > 0;
                                const isArriving =
                                  typeof remaining === 'number' &&
                                  remaining < 1 &&
                                  remaining > 0.12;
                                const isArrivedByEta =
                                  typeof remaining === 'number' && remaining <= 0.12;
                                const noEta = !hasEta && !isArriving && !isArrivedByEta;

                                // Force show ARRIVED if this location is fromLocation of the first arrived bus
                                const isPrevStopOfArrived =
                                  targetFromLocationId !== null &&
                                  targetFromLocationId === location.location.id;

                                const shouldShowArrived = isPrevStopOfArrived || isArrivedByEta;

                                const borderColor =
                                  noEta || _.isEmpty(location.busData) ? '#a6a6a6' : location.color;

                                const shouldForceNoEta = _.isEmpty(location.busData) || noEta;

                                return (
                                  <div
                                    key={location.id}
                                    className="Text-Wrapper Border-Bottom"
                                    style={{
                                      borderLeftWidth: '7px',
                                      borderLeftStyle: 'solid',
                                      borderLeftColor: borderColor,
                                    }}
                                  >
                                    <div className="Segment-Title-Wrapper">
                                      <span className="Segment-Title">
                                        {location.location.name}
                                      </span>
                                      <span className="Segment-Description">
                                        {location.location.description}
                                      </span>
                                    </div>

                                    {/* Status text */}
                                    {shouldForceNoEta ? (
                                      <span className="Text-Time">
                                        <span className="Text-Min">No ETA</span>
                                      </span>
                                    ) : shouldShowArrived ? (
                                      <span className="Text-Note">ARRIVED</span>
                                    ) : isArriving ? (
                                      <span className="Text-Note">ARRIVING</span>
                                    ) : hasEta ? (
                                      <span className="Text-Time">
                                        {Math.floor(remaining!)}
                                        <span className="Text-Min">min</span>
                                      </span>
                                    ) : (
                                      <span className="Text-Time">
                                        <span className="Text-Min">No ETA</span>
                                      </span>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                        <div className="Mailto-Wrapper">
                          <span>For any inquiry or feedback, please email us at:</span>{' '}
                          <a className="Mailto-Link" href="mailto:feedback@woodlandstransport.com.sg">
                            feedback@woodlandstransport.com.sg
                          </a>
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Only Show if layout is at least 1130px wide */}
          {width >= 1130 && (
            <div className="Map-Wrapper">
              <div className="Map-Content-Wrapper">
                <div
                  ref={mapLocationSourceRef}
                  style={{
                    minHeight: '85vh',
                    flex: 1,
                    width: '60%',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <GoogleMapReact
                    bootstrapURLKeys={{
                      key: 'AIzaSyDYGvy_HvsUfvJ_7_d4PmOH201jIFNn-iM',
                    }}
                    defaultCenter={{
                      lat: 1.328423,
                      lng: 103.746267,
                    }}
                    yesIWantToUseGoogleMapApiInternals
                    onGoogleApiLoaded={handleApiLoaded}
                    defaultZoom={14}
                  >
                    {locations
                      .sort((a: any, b: any) => a.order - b.order)
                      .map((location: any) => {
                      const now = moment().add(ntp, 'minutes');
                      let remaining: number | null = null;

                      // 1️⃣ Find smallest ETA for the active route
                      const smallestEta =
                        location?.etas
                          ?.filter(
                            (eta: any) =>
                              eta &&
                              eta.eta &&
                              eta.route.id === activeRouteId &&
                              compareTime.diff(moment(eta.expiredAt), 'minutes') < 1
                          )
                          ?.reduce((min: any, eta: any) => {
                            if (min === null) return eta;
                            return moment(eta.eta).isBefore(moment(min.eta)) ? eta : min;
                          }, null) || null;

                      // 2️⃣ Calculate remaining minutes
                      if (smallestEta && smallestEta.eta !== null) {
                        remaining = moment.duration(moment(smallestEta.eta).diff(now)).asMinutes();
                      }

                      // 3️⃣ Determine ETA states
                      const hasEta = typeof remaining === 'number' && remaining > 0;
                      const isArriving =
                        typeof remaining === 'number' && remaining < 1 && remaining > 0.12;
                      const isArrived = typeof remaining === 'number' && remaining <= 0.12;
                      const noEta = !hasEta && !isArriving && !isArrived;

                      // 4️⃣ Determine color
                      // Grey if: No ETA OR busData empty
                      // Keep color if ETA, ARRIVING, or ARRIVED
                      const markerColor =
                        noEta || _.isEmpty(location.busData) ? '#a6a6a6' : location.color;

                      // 5️⃣ Render FlagMarker
                      return (
                        <FlagMarker
                          key={location.id}
                          color={markerColor}
                          lat={location.location.lat}
                          lng={location.location.lng}
                        />
                      );
                    })}

                    {locations
                      .sort((a: any, b: any) => a.order - b.order)
                      .map((busLocation: any) => {
                      const smallestEta = busLocation?.etas
                        ?.filter((eta: any) => eta && eta.eta)
                        ?.reduce((min: any, eta: any) => {
                          if (min === null) return eta;
                          return moment(eta.eta).isBefore(moment(min.eta)) ? eta : min;
                        }, null);

                      return busLocation?.busData?.map((bus: any, i: number) => {
                        const now = moment().add(ntp, 'minutes');
                        const diff = compareTime.diff(moment(bus.vehicle?.expiredAt), 'minutes');

                        // Calculate ETA or remaining time
                        const adjustedETA =
                          smallestEta === null ? moment(bus.eta) : moment(smallestEta?.eta);
                        const remaining = moment.duration(adjustedETA.diff(now)).asMinutes();

                        // 🚫 Skip if expired
                        if (!bus.hide && diff === 2) return <></>;
                        if (bus.hide || activeRouteId !== bus.route.id) return <></>;

                        // Determine if this bus should show "ARRIVED"
                        const isPrevStopOfArrivedBus =
                          targetFromLocationId !== null &&
                          targetFromLocationId === bus.fromLocation?.id;

                        return (
                          <BusMarker
                            remaining={remaining}
                            color={busLocation.color}
                            bus={bus}
                            key={i}
                            lat={bus?.vehicle?.lastLocationLat}
                            lng={bus?.vehicle?.lastLocationLng}
                            isPrevStopOfArrivedBus={isPrevStopOfArrivedBus}
                            targetFromLocationIdColor={
                              targetFromLocationIdColor || busLocation.color
                            }
                          />
                        );
                      });
                    })}
                  </GoogleMapReact>
                </div>
                <div className="Route-Wrapper">
                  <div className="Route-Header">
                    <div className="Text-Wrapper">
                      <span className="Text-Title">Pick-up</span>
                      <span className="Text-Detail">Estimated Time of Arrival</span>
                    </div>
                  </div>
                  <div className="Route-Body">
                    {_.isEmpty(locations) ? (
                      <div
                        className="Title-Loading-Wrapper"
                        style={{ alignItems: 'center', justifyContent: 'center' }}
                      >
                        {hasLoadedRouteTags && _.isEmpty(routeTags) ? (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                            No {serviceType === 'shuttle' ? 'shuttle' : 'trunk'} service routes available.
                          </div>
                        ) : (
                          <div className="lds-ellipsis" style={{ marginLeft: '-40px' }}>
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                          </div>
                        )}
                      </div>
                    ) : (
                      locations
                        .sort((a: any, b: any) => a.order - b.order)
                        .map((location: any) => {
                        const now = moment().add(ntp, 'minutes');
                        let remaining: number | null = null;

                        // Find smallest ETA
                        const smallestEta =
                          location?.etas
                            ?.filter(
                              (eta: any) =>
                                eta &&
                                eta.eta &&
                                eta.route.id === activeRouteId &&
                                compareTime.diff(moment(eta.expiredAt), 'minutes') < 1
                            )
                            ?.reduce((min: any, eta: any) => {
                              if (min === null) return eta;
                              return moment(eta.eta).isBefore(moment(min.eta)) ? eta : min;
                            }, null) || null;

                        if (smallestEta && smallestEta.eta !== null) {
                          remaining = moment
                            .duration(moment(smallestEta.eta).diff(now))
                            .asMinutes();
                        }

                        // States
                        const hasEta = typeof remaining === 'number' && remaining > 0;
                        const isArriving =
                          typeof remaining === 'number' && remaining < 1 && remaining > 0.12;
                        const isArrivedByEta = typeof remaining === 'number' && remaining <= 0.12;
                        const noEta = !hasEta && !isArriving && !isArrivedByEta;

                        // Force show ARRIVED if this location is fromLocation of the first arrived bus
                        const isPrevStopOfArrived =
                          targetFromLocationId !== null &&
                          targetFromLocationId === location.location.id;

                        const shouldShowArrived = isPrevStopOfArrived || isArrivedByEta;

                        const borderColor =
                          noEta || _.isEmpty(location.busData) ? '#a6a6a6' : location.color;

                        const shouldForceNoEta = _.isEmpty(location.busData) || noEta;

                        return (
                          <div
                            key={location.id}
                            className="Text-Wrapper Border-Bottom"
                            style={{
                              borderLeftWidth: '7px',
                              borderLeftStyle: 'solid',
                              borderLeftColor: borderColor,
                            }}
                          >
                            <div className="Segment-Title-Wrapper">
                              <span className="Segment-Title">{location.location.name}</span>
                              <span className="Segment-Description">
                                {location.location.description}
                                {/* {JSON.stringify(location.busData)} */}
                              </span>
                            </div>

                            {/* Status text */}
                            {shouldForceNoEta ? (
                              <span className="Text-Time">
                                <span className="Text-Min">No ETA</span>
                              </span>
                            ) : shouldShowArrived ? (
                              <span className="Text-Note">ARRIVED</span>
                            ) : isArriving ? (
                              <span className="Text-Note">ARRIVING</span>
                            ) : hasEta ? (
                              <span className="Text-Time">
                                {Math.floor(remaining!)}
                                <span className="Text-Min">min</span>
                              </span>
                            ) : (
                              <span className="Text-Time">
                                <span className="Text-Min">No ETA</span>
                              </span>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="Mailto-Wrapper">
                    <span>For any inquiry or feedback, please email us at:</span>{' '}
                    <a className="Mailto-Link" href="mailto:feedback@woodlandstransport.com.sg">
                      feedback@woodlandstransport.com.sg
                    </a>
                  </div>
                </div>
              </div>

              <Footer />
            </div>
          )}
          {showModal && (
            <div className="Modal">
              <div className="Modal-Content">
                <div
                  onClick={() => {
                    setShowModal((state) => !state);
                  }}
                  className="CloseButton"
                >
                  <img alt="close" className="Close-Icon" src={closeIcon} />
                </div>
                <iframe
                  className="Modal-Iframe"
                  src="https://form.jotform.com/woodlandstransport/mandai-express"
                  title="description"
                ></iframe>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }
);

const Footer = () => {
  const { width } = useViewport();

  return (
    <div className="Footer-Wrapper">
      <div className="Logo-Container">
        <div className="Plotigo-Wrapper">
          <p>Powered by</p>
          <section>
            <a href="https://www.plotigo.app/">
              <img className="Plotigo-Logo" src={plotigoLogo} alt="plotigo-logo" />
            </a>
          </section>
        </div>
        <div className="Wt-Wrapper">
          {width > 1130 ? (
            <>
              <span style={{ marginLeft: '8px' }}>|</span> <p>Operated by</p>
            </>
          ) : (
            <p>Operated by</p>
          )}
          <section className="Wt-Wrapper">
            <a href="https://www.woodlandstransport.com.sg/">
              <img className="Wt-Logo" src={wtLogo} alt="wt-logo" />
            </a>
          </section>
        </div>
      </div>

      {/* Provide Social Media links if available */}
      {/* <div className="Social-Links">
        <a
          href="https://www.facebook.com/share/1CvQyimY3P/?mibextid=wwXIfr"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={facebookIcon} alt="facebook" />
        </a>
        <a
          href="https://www.instagram.com/sembawangsc?igsh=b2x6OG1ndDhiOXht&utm_source=qr"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={instagramIcon} alt="instagram" />
        </a>
      </div> */}
    </div>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <HashRouter>
        <Switch>
          <Route path="/m1-eta" render={() => <Main serviceType="trunk" />} />
          <Route path="/m1-shuttle-eta" render={() => <Main serviceType="shuttle" />} />
          <Redirect from="/" to="/m1-shuttle-eta" />
        </Switch>
      </HashRouter>
    </Provider>
  );
};

export default React.memo(App);
